/**
 * Type mapping utilities for different platforms
 * Consolidates type conversion logic from signature-mapper.ts
 */

export interface TypeMapping {
  typescript: string;
  swift: string;
  objectiveC: string;
  java: string;
  kotlin: string;
}

export const TYPE_MAPPINGS: Record<string, TypeMapping> = {
  string: {
    typescript: "string",
    swift: "String",
    objectiveC: "NSString *",
    java: "String",
    kotlin: "String",
  },
  number: {
    typescript: "number",
    swift: "Double",
    objectiveC: "double",
    java: "double",
    kotlin: "Double",
  },
  boolean: {
    typescript: "boolean",
    swift: "Bool",
    objectiveC: "BOOL",
    java: "boolean",
    kotlin: "Boolean",
  },
  void: {
    typescript: "void",
    swift: "Void",
    objectiveC: "void",
    java: "void",
    kotlin: "Unit",
  },
  any: {
    typescript: "any",
    swift: "Any",
    objectiveC: "id",
    java: "Object",
    kotlin: "Any",
  },
};

export type Platform = "swift" | "objectiveC" | "java" | "kotlin";

export function mapTypeScriptType(tsType: string, platform: Platform): string {
  // Handle Promise types
  if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
    const innerType = tsType.slice(8, -1);
    return mapTypeScriptType(innerType, platform);
  }

  // Handle array types
  if (tsType.endsWith("[]")) {
    const elementType = tsType.slice(0, -2);

    // For complex object arrays, we need to preserve the type information
    // instead of mapping to Any immediately
    if (elementType.startsWith("{") && elementType.endsWith("}")) {
      switch (platform) {
        case "swift":
          // Return the complex type unmapped so it can be handled by proper struct generation
          return `[${elementType}]`;
        case "objectiveC":
          return "NSArray *";
        case "java":
          return "ReadableArray";
        case "kotlin":
          return `List<ReadableMap>`;
        default:
          return "Array";
      }
    }

    const mappedElementType = mapTypeScriptType(elementType, platform);

    switch (platform) {
      case "swift":
        return `[${mappedElementType}]`;
      case "objectiveC":
        return "NSArray *";
      case "java":
        return "ReadableArray";
      case "kotlin":
        return `List<${mappedElementType}>`;
      default:
        return "Array";
    }
  }

  // Handle object types
  if (tsType.startsWith("{") && tsType.endsWith("}")) {
    switch (platform) {
      case "swift":
        // Return the complex type unmapped so it can be handled by proper struct generation
        return tsType;
      case "objectiveC":
        return "id";
      case "java":
        return "ReadableMap";
      case "kotlin":
        return "ReadableMap";
      default:
        return "Object";
    }
  }

  // Basic type mapping
  const mapping = TYPE_MAPPINGS[tsType];
  if (mapping) {
    return mapping[platform];
  }

  // Handle custom interface types as complex objects
  // These should be treated as structured objects, not generic Any types
  if (tsType.match(/^[A-Z][a-zA-Z0-9]*$/)) {
    console.log(
      `[type-mapping] Converting interface type '${tsType}' for platform '${platform}'`,
    );
    switch (platform) {
      case "swift":
        // Return the original type name to be handled by struct generation
        return tsType;
      case "objectiveC":
        return "id";
      case "java":
        return "ReadableMap";
      case "kotlin":
        return "ReadableMap";
      default:
        return "Object";
    }
  }

  // Default fallback for truly unknown types
  return TYPE_MAPPINGS.any?.[platform] || "Object";
}

export function generateMethodSignature(
  methodName: string,
  params: Array<{ name: string; type: string }>,
  returnType: string,
  platform: Platform,
  isAsync: boolean = false,
): string {
  switch (platform) {
    case "swift":
      return generateSwiftSignature(methodName, params, returnType, isAsync);
    case "objectiveC":
      return generateObjCSignature(methodName, params, returnType, isAsync);
    case "java":
      return generateJavaSignature(methodName, params, returnType, isAsync);
    case "kotlin":
      return generateKotlinSignature(methodName, params, returnType, isAsync);
    default:
      throw new Error(`Unsupported platform: ${platform}`);
  }
}

function generateSwiftSignature(
  methodName: string,
  params: Array<{ name: string; type: string }>,
  returnType: string,
  isAsync: boolean,
): string {
  const paramList = params
    .map((p) => `${p.name}: ${mapTypeScriptType(p.type, "swift")}`)
    .join(", ");
  const asyncKeyword = isAsync ? "async " : "";
  const mappedReturnType = mapTypeScriptType(returnType, "swift");

  return `func ${methodName}(${paramList}) ${asyncKeyword}throws -> ${mappedReturnType}`;
}

function generateObjCSignature(
  methodName: string,
  params: Array<{ name: string; type: string }>,
  returnType: string,
  isAsync: boolean,
): string {
  const mappedReturnType = isAsync
    ? "void"
    : mapTypeScriptType(returnType, "objectiveC");
  let signature = `- (${mappedReturnType})${methodName}`;

  if (params.length > 0) {
    const paramStrings = params.map((param, index) => {
      const objcType = mapTypeScriptType(param.type, "objectiveC");
      return index === 0
        ? `:(${objcType})${param.name}`
        : `${param.name}:(${objcType})${param.name}`;
    });
    signature += paramStrings.join(" ");
  }

  if (isAsync) {
    signature +=
      " resolve:(RCTPromiseResolveBlock)resolve reject:(RCTPromiseRejectBlock)reject";
  }

  return signature;
}

function generateJavaSignature(
  methodName: string,
  params: Array<{ name: string; type: string }>,
  returnType: string,
  isAsync: boolean,
): string {
  const mappedReturnType = isAsync
    ? "void"
    : mapTypeScriptType(returnType, "java");
  const paramList = params.map(
    (p) => `${mapTypeScriptType(p.type, "java")} ${p.name}`,
  );

  if (isAsync) {
    paramList.push("Promise promise");
  }

  return `public ${mappedReturnType} ${methodName}(${paramList.join(", ")})`;
}

function generateKotlinSignature(
  methodName: string,
  params: Array<{ name: string; type: string }>,
  returnType: string,
  isAsync: boolean,
): string {
  const mappedReturnType = isAsync
    ? "Unit"
    : mapTypeScriptType(returnType, "kotlin");
  const paramList = params.map(
    (p) => `${p.name}: ${mapTypeScriptType(p.type, "kotlin")}`,
  );

  if (isAsync) {
    paramList.push("promise: Promise");
  }

  return `fun ${methodName}(${paramList.join(", ")}): ${mappedReturnType}`;
}
